import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Star, Rocket, Bell, BellOff, Share2, Flag } from "lucide-react";

interface ListingType {
  id: string;
  title: string;
  address: string;
  price: string;
  type: string;
  status: string;
  views: number;
  inquiries: number;
  image: string;
  featured: boolean;
  boosted: boolean;
  hasUnreadMessages?: boolean;
  hasUpdates?: boolean;
}

interface ListingActionsProps {
  property: ListingType;
  onOpenFeaturedDialog: (id: string) => void;
  onOpenBoostDialog: (id: string) => void;
  onShareListing: (id: string, title: string) => void;
  onReportIssue: (id: string) => void;
  onNotificationToggle: (
    id: string,
    currentStatus: boolean | undefined
  ) => void;
}

const ListingActions = ({
  property,
  onOpenFeaturedDialog,
  onOpenBoostDialog,
  onShareListing,
  onReportIssue,
  onNotificationToggle,
}: ListingActionsProps) => {
  return (
    <div className="flex gap-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onOpenFeaturedDialog(property.id)}
            >
              <Star
                className={`h-4 w-4 ${
                  property.featured ? "fill-amber-500 text-amber-500" : ""
                }`}
              />
              <span className="sr-only">
                {property.featured
                  ? "Remove from Featured"
                  : "Mark as Featured"}
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {property.featured ? "Remove from Featured" : "Mark as Featured"}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onOpenBoostDialog(property.id)}
            >
              <Rocket
                className={`h-4 w-4 ${property.boosted ? "text-blue-500" : ""}`}
              />
              <span className="sr-only">Boost Listing</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Boost Listing</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Notification toggle button - Make more visible */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={property.hasUpdates ? "secondary" : "outline"}
              size="sm"
              className={`h-8 w-8 p-0 ${
                property.hasUpdates ? "bg-green-100" : ""
              }`}
              onClick={() =>
                onNotificationToggle(property.id, property.hasUpdates)
              }
            >
              {property.hasUpdates ? (
                <Bell className="h-4 w-4 text-green-600" />
              ) : (
                <BellOff className="h-4 w-4 text-gray-400" />
              )}
              <span className="sr-only">
                {property.hasUpdates
                  ? "Disable Notifications"
                  : "Enable Notifications"}
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {property.hasUpdates
                ? "Disable Notifications"
                : "Enable Notifications"}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Share listing button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onShareListing(property.id, property.title)}
            >
              <Share2 className="h-4 w-4" />
              <span className="sr-only">Share Listing</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Share Listing</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Report issue button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onReportIssue(property.id)}
            >
              <Flag className="h-4 w-4" />
              <span className="sr-only">Report Issue</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Report Issue</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export default ListingActions;
